// services/grid-modal-dispatcher.service.ts
// ─────────────────────────────────────────────────────────────────
// Injectable service that reads modal_config.modal_type from the
// column definition and opens the corresponding Material Dialog.
//
// The dispatcher is the ONLY place that knows about concrete dialog
// components. To add a new modal_type, add one case and one import.
// ─────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StatusTimelineModalComponent } from '../../modals/status-timeline-modal/status-timeline-modal.component';
import { TechnicianModalComponent } from '../../modals/technician-modal/technician-modal.component';
import { LaborReviewModalComponent } from '../../modals/labor-review-modal/labor-review-modal.component';
import { LadingNotesModalComponent } from '../../modals/lading-notes-modal/lading-notes-modal.component';
import { AppointmentWindowModalComponent } from '../../modals/appointment-window-modal/appointment-window-modal.component';
import { ModalConfig } from '../../models/modal-system.interfaces';

/** Data contract passed to every modal via MAT_DIALOG_DATA. */
export interface ModalDialogData {
  lading_uuid: string;
  rowData: Record<string, any>;
  modalConfig: ModalConfig;
  headerName: string;
  contact_uuid?: string;
}

@Injectable({ providedIn: 'root' })
export class GridModalDispatcherService {

  constructor(private dialog: MatDialog) {}

  /**
   * Open the correct dialog based on modal_config.modal_type.
   *
   * @param lading_uuid  — PK of the row
   * @param rowData      — full row data object from AG Grid
   * @param modalConfig  — the modal_config object from field_config
   * @param headerName   — column header (used as fallback dialog title)
   * @param contact_uuid — optional scoped carrier contact for elevated users
   * @returns MatDialogRef or null if modal_type is unrecognized
   */
  dispatch(
    lading_uuid: string,
    rowData: Record<string, any>,
    modalConfig: ModalConfig,
    headerName: string,
    contact_uuid?: string,
  ): MatDialogRef<any> | null {

    const payload: ModalDialogData = {
      lading_uuid,
      rowData,
      modalConfig,
      headerName,
      contact_uuid,
    };

    switch (modalConfig.modal_type) {

      case 'status_timeline':
        return this.dialog.open(StatusTimelineModalComponent, {
          width: modalConfig.dialog_width ?? '800px',
          maxHeight: '85vh',
          data: payload,
          autoFocus: false,
          panelClass: 'ulaap-modal-panel',
        });

      case 'technician':
        return this.dialog.open(TechnicianModalComponent, {
          width: modalConfig.dialog_width ?? '860px',
          maxHeight: '85vh',
          data: payload,
          autoFocus: false,
          panelClass: 'ulaap-modal-panel',
        });

      case 'labor_review':
        return this.dialog.open(LaborReviewModalComponent, {
          width: modalConfig.dialog_width ?? '800px',
          maxHeight: '85vh',
          data: payload,
          autoFocus: false,
          panelClass: ['ulaap-modal-panel', 'labor-review-dialog-panel'],
        });

      case 'notes':
        return this.dialog.open(LadingNotesModalComponent, {
          width: modalConfig.dialog_width ?? '800px',
          maxHeight: '85vh',
          data: payload,
          autoFocus: false,
          panelClass: 'ulaap-modal-panel',
        });

      case 'appointment_window':
        return this.dialog.open(AppointmentWindowModalComponent, {
          width: modalConfig.dialog_width ?? '540px',
          maxHeight: '85vh',
          data: payload,
          autoFocus: false,
          panelClass: 'ulaap-modal-panel',
        });

      default:
        console.error(
          `[GridModalDispatcher] Unknown modal_type: "${(modalConfig as any).modal_type}"`,
        );
        return null;
    }
  }
}
